import { jsPDF } from 'jspdf';
import { renderReactSection } from '../pdf/renderReactSection';
import { getApplicableSections } from './dailyReportSections';
import { formatReportDate, formatReportMonthYear, parseReportDateInput, toDateInputValue } from './sampleProgressForReport';
import { SCMI_COVER, DAILY_REPORT_PDF, SCMI_BRAND, HOLE_INSET_MAP, getHoleInsetMapRect } from './templateConfig';
import { aspectRatioMismatch, getPdfContentLayout, mmToPx } from './pdfLayout';
import {
  applyReportPageChrome,
  loadSelkirkLogoAsset,
} from './pdfPageChrome';
import PrintCoverPage from '../../components/dailyReport/print/PrintCoverPage';

async function loadImageAsDataUrl(url) {
  const response = await fetch(url, { cache: 'no-cache' });
  if (!response.ok) {
    throw new Error(`Cover image not found (${response.status})`);
  }
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function measureImageDataUrl(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
      });
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}

function addAlignedText(pdf, text, { xMm, yMm, fontSize, align = 'left', color = [255, 255, 255], bold = false }) {
  pdf.setFontSize(fontSize);
  pdf.setFont(undefined, bold ? 'bold' : 'normal');
  pdf.setTextColor(color[0], color[1], color[2]);
  pdf.text(text, xMm, yMm, { align });
}

async function addTemplateCoverPage(pdf, { reportDate, reportDateLabel, campaignLabel, onProgress, printContainerRef, layout }) {
  const { pageWidth, pageHeight, marginMm, contentWidthMm, contentWidthPx } = layout;
  const monthYearLabel = formatReportMonthYear(reportDate, { uppercase: true });

  try {
    onProgress('Loading cover template...');
    const dataUrl = await loadImageAsDataUrl(SCMI_COVER.backgroundUrl);
    const dims = await measureImageDataUrl(dataUrl);

    if (
      aspectRatioMismatch(dims.width, dims.height, pageWidth, pageHeight) &&
      process.env.NODE_ENV !== 'production'
    ) {
      console.warn(
        '[dailyReportPdfGenerator] Cover aspect ratio differs from page:',
        { image: dims.width / dims.height, page: pageWidth / pageHeight }
      );
    }

    pdf.addImage(dataUrl, 'PNG', 0, 0, pageWidth, pageHeight);

    if (SCMI_COVER.title?.text) {
      addAlignedText(pdf, SCMI_COVER.title.text, {
        ...SCMI_COVER.title,
        bold: SCMI_COVER.title.bold !== false,
      });
    }

    addAlignedText(pdf, reportDateLabel, {
      ...SCMI_COVER.date,
      bold: SCMI_COVER.date.bold !== false,
    });

    if (campaignLabel) {
      addAlignedText(pdf, campaignLabel, {
        ...SCMI_COVER.subtitle,
        bold: SCMI_COVER.subtitle.bold !== false,
      });
    }

    if (monthYearLabel && SCMI_COVER.footerMonthYear) {
      addAlignedText(pdf, monthYearLabel, {
        ...SCMI_COVER.footerMonthYear,
        bold: true,
      });
    }
  } catch (error) {
    console.warn('Template cover unavailable, using fallback:', error.message);
    onProgress('Rendering fallback cover page...');
    await renderReactSection(
      pdf,
      PrintCoverPage,
      {
        reportDateLabel,
        campaignLabel,
        reportTitle: DAILY_REPORT_PDF.reportTitle,
      },
      printContainerRef,
      marginMm,
      contentWidthMm,
      {
        topOffsetMm: 0,
        renderWaitMs: 500,
        bottomReserveMm: 0,
        captureWidthPx: contentWidthPx,
      }
    );
  }
}

function addHoleInsetMapOverlay(pdf, insetMapDataUrl, layout) {
  if (!insetMapDataUrl) return;

  const { marginMm, contentWidthMm, contentTopMm } = layout;
  const { xMm, yMm, widthMm, heightMm } = getHoleInsetMapRect(
    marginMm,
    contentWidthMm,
    contentTopMm
  );
  const labelBarHeightMm = HOLE_INSET_MAP.labelBarHeightMm ?? 4;

  pdf.addImage(insetMapDataUrl, 'PNG', xMm, yMm, widthMm, heightMm);
  pdf.setDrawColor(209, 213, 219);
  pdf.setLineWidth(0.25);
  pdf.rect(xMm, yMm, widthMm, heightMm, 'S');
  pdf.setFillColor(17, 24, 39);
  pdf.rect(xMm, yMm + heightMm - labelBarHeightMm, widthMm, labelBarHeightMm, 'F');
  pdf.setFontSize(5.5);
  pdf.setFont(undefined, 'bold');
  pdf.setTextColor(249, 250, 251);
  pdf.text(`${HOLE_INSET_MAP.spanM} m`, xMm + widthMm - 1.5, yMm + heightMm - 1.2, {
    align: 'right',
  });
}

/**
 * Generate SCMI Daily Report PDF.
 */
export async function generateDailyReport(options) {
  const {
    reportDate,
    campaignLabel,
    sampleMetrics,
    drillingMetrics,
    narrative,
    selectedSections,
    mapReportContext,
    inProgressHolePages,
    hasInProgressSelection,
    onProgress = () => {},
    printContainerRef,
  } = options;

  if (!printContainerRef) {
    throw new Error('Print container element is required');
  }

  const reportDateLabel = formatReportDate(reportDate);
  const context = {
    sampleMetrics,
    drillingMetrics,
    narrative,
    campaignLabel,
    reportDateLabel,
    mapReportContext,
    inProgressHolePages,
    hasInProgressSelection,
  };
  const sections = getApplicableSections(selectedSections, context);

  if (sections.length === 0) {
    throw new Error('Select at least one report section');
  }

  onProgress('Initializing PDF document...');

  const pdf = new jsPDF({
    orientation: DAILY_REPORT_PDF.orientation,
    unit: 'mm',
    format: DAILY_REPORT_PDF.format,
  });
  const layout = getPdfContentLayout(pdf, {
    marginMm: DAILY_REPORT_PDF.marginMm,
    contentTopMm: SCMI_BRAND.contentTopMm,
    bottomReserveMm: SCMI_BRAND.footerReserveMm,
  });
  const { marginMm, contentWidthMm, contentWidthPx, contentTopMm, bottomReserveMm } = layout;
  const logoAsset = await loadSelkirkLogoAsset();

  let contentPageNumber = 0;
  let isFirstPage = true;

  for (const section of sections) {
    if (section.renderType === 'cover') {
      if (!isFirstPage) {
        pdf.addPage(DAILY_REPORT_PDF.format, DAILY_REPORT_PDF.orientation);
      }
      await addTemplateCoverPage(pdf, {
        reportDate,
        reportDateLabel,
        campaignLabel,
        onProgress,
        printContainerRef,
        layout,
      });
      isFirstPage = false;
      continue;
    }

    if (section.renderType === 'react' && section.Component) {
      if (!isFirstPage) {
        pdf.addPage(DAILY_REPORT_PDF.format, DAILY_REPORT_PDF.orientation);
      } else {
        isFirstPage = false;
      }

      contentPageNumber += 1;
      applyReportPageChrome(pdf, contentPageNumber, logoAsset, {
        marginMm,
        pageTitle: section.pageTitle || section.label,
        pageSubtitle: section.getPageSubtitle?.(context) ?? null,
      });

      onProgress(`Rendering ${section.label}...`);
      const props = section.getProps ? section.getProps(context) : {};
      const pageHeight = pdf.internal.pageSize.getHeight();
      const availableContentHeightMm = pageHeight - contentTopMm - bottomReserveMm;
      await renderReactSection(
        pdf,
        section.Component,
        props,
        printContainerRef,
        marginMm,
        contentWidthMm,
        {
          topOffsetMm: contentTopMm,
          bottomReserveMm,
          captureWidthPx: contentWidthPx,
          minCaptureHeightPx: mmToPx(availableContentHeightMm),
          renderWaitMs: 500,
          contentPaddingPx: section.contentPaddingPx,
        }
      );
    } else if (section.renderType === 'holePages' && section.Component) {
      const holePages = section.getPages ? section.getPages(context) : [];

      for (const holePage of holePages) {
        if (!isFirstPage) {
          pdf.addPage(DAILY_REPORT_PDF.format, DAILY_REPORT_PDF.orientation);
        } else {
          isFirstPage = false;
        }

        contentPageNumber += 1;
        applyReportPageChrome(pdf, contentPageNumber, logoAsset, {
          marginMm,
          pageTitle: section.getPageTitle?.(holePage) ?? section.label,
          pageSubtitle: section.getPageSubtitle?.(holePage) ?? null,
        });

        onProgress(`Rendering ${section.getPageTitle?.(holePage) ?? section.label}...`);
        const pageHeight = pdf.internal.pageSize.getHeight();
        const availableContentHeightMm = pageHeight - contentTopMm - bottomReserveMm;
        const fixedCaptureHeightPx = mmToPx(availableContentHeightMm);
        const { insetMapDataUrl, ...holePageProps } = holePage;

        await renderReactSection(
          pdf,
          section.Component,
          holePageProps,
          printContainerRef,
          marginMm,
          contentWidthMm,
          {
            topOffsetMm: contentTopMm,
            bottomReserveMm,
            captureWidthPx: contentWidthPx,
            fixedCaptureHeightPx,
            renderWaitMs: 500,
            contentPaddingPx: section.contentPaddingPx,
          }
        );

        addHoleInsetMapOverlay(pdf, insetMapDataUrl, {
          marginMm,
          contentWidthMm,
          contentTopMm,
        });
      }
    }
  }

  onProgress('Finalizing PDF...');
  const parsedDate = parseReportDateInput(reportDate);
  const datePart = parsedDate
    ? toDateInputValue(parsedDate)
    : typeof reportDate === 'string' && reportDate.length >= 10
      ? reportDate.slice(0, 10)
      : new Date().toISOString().split('T')[0];
  pdf.save(`SCMI_Daily_Report_${datePart}.pdf`);
  onProgress('Download complete!');
}

export default generateDailyReport;
